import React from 'react';
import SEO from '../components/SEO';
import {
  Box,
  Container,
  Typography,
  Grid,
  Chip,
  Divider,
} from '@mui/material';
import PrivacyTipRoundedIcon from '@mui/icons-material/PrivacyTipRounded';
import DataUsageRoundedIcon from '@mui/icons-material/DataUsageRounded';
import HandshakeRoundedIcon from '@mui/icons-material/HandshakeRounded';
import VerifiedUserRoundedIcon from '@mui/icons-material/VerifiedUserRounded';
import SettingsSuggestRoundedIcon from '@mui/icons-material/SettingsSuggestRounded';
import MailOutlineRoundedIcon from '@mui/icons-material/MailOutlineRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import PaymentRoundedIcon from '@mui/icons-material/PaymentRounded';
import CookieRoundedIcon from '@mui/icons-material/CookieRounded';
import ChildCareRoundedIcon from '@mui/icons-material/ChildCareRounded';
import GavelRoundedIcon from '@mui/icons-material/GavelRounded';
import PageBanner from '../components/PageBanner';

const policyCards = [
  {
    title: 'Information We Collect',
    icon: DataUsageRoundedIcon,
    gradient: 'linear-gradient(135deg, #0ea5e9, #2563eb)',
    points: [
      'Personal details: name, phone number, email address, and postal address when you register, submit forms, or contact the temple office.',
      'Account information: login credentials (email and password or Google account) when you create an account on our website.',
      'Booking and donation details: pooja names, dates, star/nakshatra details, family member names, donation amounts, and offering preferences.',
      'Payment information: transaction IDs, order IDs, and payment status. We do not store card numbers, CVV, or banking credentials — these are handled entirely by Razorpay.',
      'Feedback and communication: messages, ratings, feedback categories, and any correspondence you send through our contact or feedback forms.',
      'Technical data: browser type, device information, IP address, pages visited, and timestamps collected automatically for site performance, security, and analytics.',
    ],
  },
  {
    title: 'How We Use Your Information',
    icon: SettingsSuggestRoundedIcon,
    gradient: 'linear-gradient(135deg, #14b8a6, #0f766e)',
    points: [
      'To process and confirm pooja bookings, vazhipad orders, donations, and related temple services.',
      'To send booking confirmations, payment receipts, and service-related notifications.',
      'To communicate temple updates, festival announcements, upcoming events, and important notices.',
      'To respond to your inquiries, feedback, and support requests submitted through the website.',
      'To maintain your devotee account, order history, and booking records for your reference.',
      'To improve website functionality, user experience, content relevance, and service reliability.',
      'To detect, prevent, and address fraud, security issues, and technical problems.',
    ],
  },
  {
    title: 'Data Sharing & Third Parties',
    icon: HandshakeRoundedIcon,
    gradient: 'linear-gradient(135deg, #f59e0b, #d97706)',
    points: [
      'We do not sell, rent, or trade your personal information to any third party for marketing purposes.',
      'Payment processing: transaction data is shared with Razorpay (our payment gateway) solely to process your payments securely. Razorpay operates under its own privacy policy and PCI-DSS compliance.',
      'Hosting and infrastructure: data is stored on Firebase (Google Cloud) with industry-standard security, encryption at rest, and encryption in transit.',
      'Bot protection: Cloudflare Turnstile is used on forms to verify human users. Cloudflare may collect limited technical data as part of this process.',
      'Information may be disclosed if required by Indian law, court order, or valid legal process by an authorized government authority.',
      'We do not share your data with any advertising networks or third-party analytics services that identify individual users.',
    ],
  },
  {
    title: 'Data Protection & Security',
    icon: VerifiedUserRoundedIcon,
    gradient: 'linear-gradient(135deg, #a855f7, #7e22ce)',
    points: [
      'All data transmission between your browser and our servers is encrypted using TLS/SSL (HTTPS).',
      'Firebase Authentication secures your account with industry-standard protocols including OAuth 2.0 for Google Sign-In.',
      'Firestore security rules restrict data access — users can only read their own bookings, donations, and payment records.',
      'Admin access is controlled through Firebase custom claims; only authorized temple administrators can access devotee records.',
      'Content Security Policy (CSP) headers are enforced to prevent cross-site scripting (XSS) and code injection attacks.',
      'While we implement strong safeguards, no internet system is 100% secure. We continuously review and improve our security measures.',
    ],
  },
];

const paymentPolicyPoints = [
  'All online payments on this website are processed through Razorpay, a PCI-DSS compliant payment gateway authorized by the Reserve Bank of India (RBI).',
  'We do not collect, store, or have access to your credit/debit card numbers, UPI PINs, net banking passwords, or any sensitive financial credentials.',
  'Only transaction metadata is stored on our side: order ID, payment ID, amount, payment status (success/failed/pending), and timestamp — for record-keeping and dispute resolution.',
  'Payment receipts and booking confirmations are generated based on successful payment verification through secure server-to-server communication between our backend and Razorpay.',
  'In case of payment disputes, failed transactions, or refund requests, please contact the temple administration with your payment ID. Refunds, if applicable, will be processed within 5–7 business days to the original payment method.',
  'By making a payment on this website, you agree to Razorpay\'s terms of service and privacy policy in addition to ours.',
];

const cookiePoints = [
  'Authentication tokens: to keep you logged in securely during your session.',
  'Firebase session data: to maintain your account state and preferences.',
  'Cloudflare Turnstile tokens: temporary tokens used for bot protection on forms (not used for tracking).',
  'We do not use advertising cookies, third-party tracking cookies, or behavioral profiling cookies.',
  'You can clear cookies through your browser settings at any time. Note that clearing authentication cookies will require you to log in again.',
];

const rights = [
  'Access: Request a copy of the personal information we hold about you.',
  'Correction: Request correction of inaccurate or incomplete personal information.',
  'Deletion: Request deletion of your personal data, subject to legal retention requirements (e.g., payment records may be retained as required by Indian tax laws).',
  'Opt out: Unsubscribe from non-essential communications and push notifications at any time.',
  'Data portability: Request your booking and donation history in a readable format.',
  'Withdraw consent: You may withdraw consent for data processing at any time by contacting us, though this may affect your ability to use certain services.',
  'Grievance redressal: Contact the temple administration for any privacy-related complaints or concerns.',
];

const childrenPoints = [
  'This website is not directed at children under the age of 13. We do not knowingly collect personal information from children under 13.',
  'Pooja bookings that include family members\' or children\'s names are submitted by the parent/guardian and are covered under their consent.',
  'If we become aware that personal data of a child under 13 has been collected without parental consent, we will take steps to delete it promptly.',
];

const legalPoints = [
  'This privacy policy is governed by the laws of India, including the Information Technology Act, 2000, and the Information Technology (Reasonable Security Practices and Procedures and Sensitive Personal Data or Information) Rules, 2011.',
  'We reserve the right to update this privacy policy at any time. Changes will be reflected on this page with an updated "Last revised" date.',
  'Continued use of the website after any changes constitutes your acceptance of the updated privacy policy.',
  'For disputes related to privacy, the jurisdiction shall be the courts located in Kozhikode, Kerala, India.',
];

export default function PrivacyPolicy() {
  return (
    <Box sx={{ bgcolor: '#f3f7fb', minHeight: '100vh' }}>
      <SEO
        title="Privacy Policy — Kottayil Temple | സ്വകാര്യതാ നയം"
        description="Privacy policy for Muchukunnu Sri Kotta-Kovilakam Kshethram website. Learn how we collect, use, and protect your personal information including payment data."
        path="/privacy-policy"
        keywords="privacy policy, data protection, temple website privacy, Razorpay payment privacy, donation privacy, pooja booking privacy, Kottayil temple"
      />
      <PageBanner title="Privacy Policy" />
      <Container maxWidth="lg" sx={{ mt: { xs: -4, md: -6 }, position: 'relative', zIndex: 2, pb: 10 }}>

        {/* Hero Section */}
        <Box sx={{ borderRadius: '28px', overflow: 'hidden', boxShadow: '0 24px 70px rgba(15,23,42,0.1)', mb: 3.5 }}>
          <Box
            sx={{
              px: { xs: 2.8, sm: 4 },
              py: { xs: 3.2, sm: 4.2 },
              background: 'linear-gradient(135deg, #0b1220 0%, #13253d 45%, #1d4b71 100%)',
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            <Box sx={{ position: 'absolute', top: -40, right: -30, width: 190, height: 190, borderRadius: '50%', background: 'radial-gradient(circle, rgba(255,255,255,0.12) 0%, transparent 70%)' }} />
            <Box sx={{ position: 'absolute', bottom: -70, left: -50, width: 230, height: 230, borderRadius: '50%', background: 'radial-gradient(circle, rgba(20,184,166,0.2) 0%, transparent 72%)' }} />
            <Box sx={{ position: 'relative', zIndex: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.4, mb: 1.6 }}>
                <Box sx={{ width: 46, height: 46, borderRadius: '14px', bgcolor: 'rgba(255,255,255,0.14)', border: '1px solid rgba(255,255,255,0.22)', backdropFilter: 'blur(8px)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <PrivacyTipRoundedIcon sx={{ color: '#fff', fontSize: 24 }} />
                </Box>
                <Typography sx={{ color: '#e2e8f0', fontWeight: 700, fontSize: '0.78rem', letterSpacing: 1.1 }}>
                  TRANSPARENCY & TRUST
                </Typography>
              </Box>
              <Typography sx={{ color: '#fff', fontWeight: 900, fontSize: { xs: '1.5rem', sm: '2rem' }, lineHeight: 1.15, mb: 1 }}>
                Your Privacy Matters
              </Typography>
              <Typography sx={{ color: 'rgba(226,232,240,0.72)', fontSize: { xs: '0.84rem', sm: '0.92rem' }, lineHeight: 1.85, maxWidth: 680, mb: 1 }}>
                This policy explains how Muchukunnu Sri Kotta-Kovilakam Kshethram (Kottayil Temple) collects, uses, safeguards, and handles your personal information when you interact with our website at kottakovilakam.com.
              </Typography>
              <Typography sx={{ color: 'rgba(226,232,240,0.55)', fontSize: '0.8rem', lineHeight: 1.7, maxWidth: 680, mb: 2.3 }}>
                By using this website, creating an account, making a donation, or booking a pooja, you consent to the data practices described in this policy.
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Chip label="Last revised: March 19, 2026" sx={{ bgcolor: 'rgba(255,255,255,0.14)', color: '#e2e8f0', fontWeight: 700, height: 30 }} />
                <Chip label="PCI-DSS compliant payments" sx={{ bgcolor: 'rgba(20,184,166,0.22)', color: '#ccfbf1', fontWeight: 700, height: 30 }} />
                <Chip label="No data selling" sx={{ bgcolor: 'rgba(56,189,248,0.2)', color: '#e0f2fe', fontWeight: 700, height: 30 }} />
                <Chip label="SSL encrypted" sx={{ bgcolor: 'rgba(168,85,247,0.2)', color: '#e9d5ff', fontWeight: 700, height: 30 }} />
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Main Policy Cards */}
        <Grid container spacing={2.5}>
          {policyCards.map((card) => {
            const Icon = card.icon;
            return (
              <Grid item xs={12} md={6} key={card.title}>
                <Box
                  sx={{
                    height: '100%',
                    bgcolor: '#fff',
                    borderRadius: '22px',
                    overflow: 'hidden',
                    border: '1px solid rgba(15,23,42,0.08)',
                    boxShadow: '0 6px 26px rgba(15,23,42,0.06)',
                    transition: 'all 280ms ease',
                    '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 16px 40px rgba(15,23,42,0.1)' },
                  }}
                >
                  <Box sx={{ px: 2.4, py: 1.8, background: card.gradient, display: 'flex', alignItems: 'center', gap: 1.2 }}>
                    <Box sx={{ width: 36, height: 36, borderRadius: '12px', bgcolor: 'rgba(255,255,255,0.2)', border: '1px solid rgba(255,255,255,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Icon sx={{ color: '#fff', fontSize: 19 }} />
                    </Box>
                    <Typography sx={{ color: '#fff', fontSize: '0.9rem', fontWeight: 800 }}>
                      {card.title}
                    </Typography>
                  </Box>
                  <Box sx={{ p: { xs: 2.3, sm: 2.6 } }}>
                    {card.points.map((point) => (
                      <Box key={point} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1.3, '&:last-child': { mb: 0 } }}>
                        <CheckCircleRoundedIcon sx={{ fontSize: 16, color: '#0ea5e9', mt: 0.35, flexShrink: 0 }} />
                        <Typography sx={{ color: '#526172', fontSize: '0.82rem', lineHeight: 1.8 }}>
                          {point}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Grid>
            );
          })}
        </Grid>

        {/* Payment & Financial Data Policy */}
        <Box
          sx={{
            mt: 3,
            bgcolor: '#fff',
            borderRadius: '22px',
            overflow: 'hidden',
            border: '1px solid rgba(15,23,42,0.08)',
            boxShadow: '0 6px 26px rgba(15,23,42,0.06)',
          }}
        >
          <Box sx={{ px: 2.4, py: 1.8, background: 'linear-gradient(135deg, #059669, #047857)', display: 'flex', alignItems: 'center', gap: 1.2 }}>
            <Box sx={{ width: 36, height: 36, borderRadius: '12px', bgcolor: 'rgba(255,255,255,0.2)', border: '1px solid rgba(255,255,255,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <PaymentRoundedIcon sx={{ color: '#fff', fontSize: 19 }} />
            </Box>
            <Typography sx={{ color: '#fff', fontSize: '0.9rem', fontWeight: 800 }}>
              Payment & Financial Data Policy
            </Typography>
          </Box>
          <Box sx={{ p: { xs: 2.3, sm: 2.6 } }}>
            {paymentPolicyPoints.map((point) => (
              <Box key={point} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1.3, '&:last-child': { mb: 0 } }}>
                <CheckCircleRoundedIcon sx={{ fontSize: 16, color: '#059669', mt: 0.35, flexShrink: 0 }} />
                <Typography sx={{ color: '#526172', fontSize: '0.82rem', lineHeight: 1.8 }}>
                  {point}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

        {/* Cookies & Local Storage */}
        <Box
          sx={{
            mt: 2.5,
            bgcolor: '#fff',
            borderRadius: '22px',
            overflow: 'hidden',
            border: '1px solid rgba(15,23,42,0.08)',
            boxShadow: '0 6px 26px rgba(15,23,42,0.06)',
          }}
        >
          <Box sx={{ px: 2.4, py: 1.8, background: 'linear-gradient(135deg, #ea580c, #c2410c)', display: 'flex', alignItems: 'center', gap: 1.2 }}>
            <Box sx={{ width: 36, height: 36, borderRadius: '12px', bgcolor: 'rgba(255,255,255,0.2)', border: '1px solid rgba(255,255,255,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <CookieRoundedIcon sx={{ color: '#fff', fontSize: 19 }} />
            </Box>
            <Typography sx={{ color: '#fff', fontSize: '0.9rem', fontWeight: 800 }}>
              Cookies & Local Storage
            </Typography>
          </Box>
          <Box sx={{ p: { xs: 2.3, sm: 2.6 } }}>
            <Typography sx={{ color: '#526172', fontSize: '0.82rem', lineHeight: 1.8, mb: 1.5 }}>
              This website uses minimal cookies and local storage strictly for functionality. We use:
            </Typography>
            {cookiePoints.map((point) => (
              <Box key={point} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1.3, '&:last-child': { mb: 0 } }}>
                <CheckCircleRoundedIcon sx={{ fontSize: 16, color: '#ea580c', mt: 0.35, flexShrink: 0 }} />
                <Typography sx={{ color: '#526172', fontSize: '0.82rem', lineHeight: 1.8 }}>
                  {point}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

        {/* Rights & Children's Privacy row */}
        <Grid container spacing={2.5} sx={{ mt: 0.2 }}>
          <Grid item xs={12} md={7}>
            <Box
              sx={{
                bgcolor: '#fff',
                borderRadius: '22px',
                border: '1px solid rgba(15,23,42,0.08)',
                boxShadow: '0 6px 26px rgba(15,23,42,0.06)',
                p: { xs: 2.5, sm: 3 },
                height: '100%',
              }}
            >
              <Typography sx={{ fontWeight: 800, color: '#0f172a', fontSize: '0.98rem', mb: 1.7 }}>
                Your Rights & Choices
              </Typography>
              {rights.map((item) => (
                <Box key={item} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1.2, '&:last-child': { mb: 0 } }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#14b8a6', mt: 1, flexShrink: 0 }} />
                  <Typography sx={{ color: '#5b6878', fontSize: '0.84rem', lineHeight: 1.8 }}>
                    {item}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Grid>

          <Grid item xs={12} md={5}>
            <Box
              sx={{
                bgcolor: '#fff',
                borderRadius: '22px',
                overflow: 'hidden',
                border: '1px solid rgba(15,23,42,0.08)',
                boxShadow: '0 6px 26px rgba(15,23,42,0.06)',
                height: '100%',
              }}
            >
              <Box sx={{ px: 2.4, py: 1.8, background: 'linear-gradient(135deg, #ec4899, #be185d)', display: 'flex', alignItems: 'center', gap: 1.2 }}>
                <Box sx={{ width: 36, height: 36, borderRadius: '12px', bgcolor: 'rgba(255,255,255,0.2)', border: '1px solid rgba(255,255,255,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <ChildCareRoundedIcon sx={{ color: '#fff', fontSize: 19 }} />
                </Box>
                <Typography sx={{ color: '#fff', fontSize: '0.9rem', fontWeight: 800 }}>
                  Children's Privacy
                </Typography>
              </Box>
              <Box sx={{ p: { xs: 2.3, sm: 2.6 } }}>
                {childrenPoints.map((point) => (
                  <Box key={point} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1.3, '&:last-child': { mb: 0 } }}>
                    <CheckCircleRoundedIcon sx={{ fontSize: 16, color: '#ec4899', mt: 0.35, flexShrink: 0 }} />
                    <Typography sx={{ color: '#526172', fontSize: '0.82rem', lineHeight: 1.8 }}>
                      {point}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          </Grid>
        </Grid>

        {/* Governing Law */}
        <Box
          sx={{
            mt: 2.5,
            bgcolor: '#fff',
            borderRadius: '22px',
            overflow: 'hidden',
            border: '1px solid rgba(15,23,42,0.08)',
            boxShadow: '0 6px 26px rgba(15,23,42,0.06)',
          }}
        >
          <Box sx={{ px: 2.4, py: 1.8, background: 'linear-gradient(135deg, #6366f1, #4338ca)', display: 'flex', alignItems: 'center', gap: 1.2 }}>
            <Box sx={{ width: 36, height: 36, borderRadius: '12px', bgcolor: 'rgba(255,255,255,0.2)', border: '1px solid rgba(255,255,255,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <GavelRoundedIcon sx={{ color: '#fff', fontSize: 19 }} />
            </Box>
            <Typography sx={{ color: '#fff', fontSize: '0.9rem', fontWeight: 800 }}>
              Governing Law & Changes
            </Typography>
          </Box>
          <Box sx={{ p: { xs: 2.3, sm: 2.6 } }}>
            {legalPoints.map((point) => (
              <Box key={point} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1.3, '&:last-child': { mb: 0 } }}>
                <CheckCircleRoundedIcon sx={{ fontSize: 16, color: '#6366f1', mt: 0.35, flexShrink: 0 }} />
                <Typography sx={{ color: '#526172', fontSize: '0.82rem', lineHeight: 1.8 }}>
                  {point}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

        {/* Privacy Contact */}
        <Box
          sx={{
            mt: 2.5,
            borderRadius: '22px',
            overflow: 'hidden',
            boxShadow: '0 10px 30px rgba(2,132,199,0.18)',
            border: '1px solid rgba(255,255,255,0.18)',
            background: 'linear-gradient(145deg, #0f766e, #0f172a)',
            p: { xs: 2.5, sm: 3 },
            position: 'relative',
          }}
        >
          <Box sx={{ position: 'absolute', top: -30, right: -20, width: 120, height: 120, borderRadius: '50%', background: 'radial-gradient(circle, rgba(255,255,255,0.16) 0%, transparent 70%)' }} />
          <Box sx={{ position: 'absolute', bottom: -40, left: -30, width: 160, height: 160, borderRadius: '50%', background: 'radial-gradient(circle, rgba(20,184,166,0.15) 0%, transparent 70%)' }} />
          <Box sx={{ position: 'relative', zIndex: 2 }}>
            <Box sx={{ width: 42, height: 42, borderRadius: '12px', bgcolor: 'rgba(255,255,255,0.16)', border: '1px solid rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1.3 }}>
              <MailOutlineRoundedIcon sx={{ color: '#ecfeff', fontSize: 22 }} />
            </Box>
            <Typography sx={{ color: '#ecfeff', fontWeight: 800, fontSize: { xs: '0.95rem', md: '1.1rem' }, mb: 0.8 }}>
              Privacy & Grievance Contact
            </Typography>
            <Typography sx={{ color: 'rgba(236,254,255,0.75)', fontSize: '0.82rem', lineHeight: 1.8, mb: 0.5 }}>
              For any privacy-related questions, data access requests, payment disputes, or grievances, please contact the temple administration:
            </Typography>
            <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)', my: 1.5 }} />
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography sx={{ color: 'rgba(236,254,255,0.5)', fontSize: '0.72rem', fontWeight: 700, letterSpacing: 0.5, mb: 0.3 }}>ORGANIZATION</Typography>
                <Typography sx={{ color: '#ecfeff', fontSize: '0.84rem', fontWeight: 600, lineHeight: 1.7 }}>
                  Muchukunnu Sri Kotta-Kovilakam Kshethram
                </Typography>
                <Typography sx={{ color: 'rgba(236,254,255,0.6)', fontSize: '0.8rem', lineHeight: 1.7 }}>
                  Muchukunnu, Kozhikode, Kerala, India
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography sx={{ color: 'rgba(236,254,255,0.5)', fontSize: '0.72rem', fontWeight: 700, letterSpacing: 0.5, mb: 0.3 }}>CONTACT</Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8 }}>
                  <Box
                    component="a"
                    href="mailto:info@kottakovilakam.com"
                    sx={{
                      display: 'inline-block',
                      px: 1.6,
                      py: 0.7,
                      borderRadius: '10px',
                      bgcolor: 'rgba(255,255,255,0.94)',
                      color: '#0f172a',
                      textDecoration: 'none',
                      fontWeight: 800,
                      fontSize: '0.8rem',
                      transition: 'all 220ms ease',
                      width: 'fit-content',
                      '&:hover': { transform: 'translateY(-2px)', bgcolor: '#fff' },
                    }}
                  >
                    info@kottakovilakam.com
                  </Box>
                  <Typography sx={{ color: 'rgba(236,254,255,0.6)', fontSize: '0.8rem' }}>
                    Website: kottakovilakam.com
                  </Typography>
                </Box>
              </Grid>
            </Grid>
            <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)', my: 1.5 }} />
            <Typography sx={{ color: 'rgba(236,254,255,0.45)', fontSize: '0.75rem', lineHeight: 1.7 }}>
              We aim to respond to all privacy-related inquiries within 7 working days. For urgent payment-related issues, please include your transaction/payment ID in the subject line for faster resolution.
            </Typography>
          </Box>
        </Box>

      </Container>
    </Box>
  );
}
